import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { History, Loader2, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight } from "lucide-react";
import { inventoryApi } from "./inventoryApi";
import { TRANSACTION_LABELS, formatDateTime } from "./inventoryConstants";
import { useT } from "@/i18n/LanguageContext";

export const TransactionLedger = () => {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const { t } = useT();

  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.transaction_type = type;
      setTxns(await inventoryApi.getTransactions(params));
    } catch { toast.error("Failed to load ledger"); }
    setLoading(false);
  }, [type]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  return (
    <div className="space-y-4" data-testid="transaction-ledger">
      <div className="flex items-center justify-between gap-2">
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white min-w-0" value={type} onChange={(e) => setType(e.target.value)} data-testid="ledger-type-filter">
          <option value="">{t("All Transaction Types")}</option>
          {Object.entries(TRANSACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-sm text-slate-500 shrink-0">{txns.length} {t("entries")}</span>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : txns.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><History className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>{t("No transactions recorded")}</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {txns.map((tx) => {
            const cfg = TRANSACTION_LABELS[tx.transaction_type] || { label: tx.transaction_type, color: "text-slate-700", bg: "bg-slate-100" };
            const up = (tx.quantity || 0) > 0;
            const down = (tx.quantity || 0) < 0;
            const chipBg = up ? "bg-green-50" : down ? "bg-red-50" : "bg-slate-100";
            const Icon = up ? ArrowDownCircle : down ? ArrowUpCircle : ArrowLeftRight;
            const iconColor = up ? "text-green-600" : down ? "text-red-600" : "text-slate-500";
            return (
              <div key={tx.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid="ledger-row">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${chipBg}`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-slate-900 truncate">{tx.part_name}</span>
                    {tx.part_number && <span className="text-[10px] font-mono text-slate-400 shrink-0 hidden sm:inline">{tx.part_number}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-slate-400"> · {formatDateTime(tx.date)} · {tx.user}{tx.reason ? ` · ${tx.reason}` : ""}{tx.unit_number ? ` · Unit ${tx.unit_number}` : ""}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-semibold tabular-nums ${up ? "text-green-600" : down ? "text-red-600" : "text-slate-500"}`}>{up ? "+" : ""}{tx.quantity}</div>
                  <div className="text-[10px] text-slate-400 tabular-nums">{tx.previous_quantity} → {tx.new_quantity}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
